import { useEffect, useMemo } from "react";

import { SectionRole } from "../../../../shared/types/roles";

import { serverGetPageAssessmentStatuses } from "api/api-server-data";
import { useCapabilities } from "store/capability-hooks";
import { storeApi, useAppDispatch, useAppSelector } from "store/index";
import {
  sectionUserSelectors,
  selectCurrentSectionId,
  selectCurrentSectionUserId,
  selectCurrentUserId,
  selectUserAssessments,
} from "store/selectors";
import { computedStateActions } from "store/slices/computed";
import { useListSelector } from "store/store-hooks";

/**
 * Ensures all user/course data that is required to resolve assessment data is fetched.
 * This does _not_ include assessment / submission / outcome-assessment data directly.
 */
export const useFetchAssessmentBaseData = () => {
  const { canParticipateInSection } = useCapabilities();
  const sectionId = useAppSelector(selectCurrentSectionId);
  const userId = useAppSelector(selectCurrentUserId);
  const sectionUserId = useAppSelector(selectCurrentSectionUserId);

  // Fetching all assessment-relevant data for the current user
  useEffect(() => {
    if (!sectionUserId || !userId) {
      // This prevents an unecessary load of all course data for students while
      // the section users are still loading.
      return;
    }
    if (sectionId) {
      storeApi.outcomes.list({});
      storeApi.section_outcomes.list({ section_id: sectionId });
      storeApi.pages.list({
        section_id: sectionId,
        // Technically we only need the unreleased pages, but we already fetch all pages in other
        // views, so dropping this filter attribute allows us to reuse the cached data.
        // released_at__isnull: false,
      });
      // Only to resolve links to pages
      storeApi.topics.list({ section_id: sectionId });
      storeApi.activities.list({ section_id: sectionId });

      // This will only trigger for students, for now, as staff users will need access to _everyone's_
      // data, which is too much to load on the top level of the course.
      // If a userId is passed, it means an instructor is viewing a student's assessments, so we still
      // fetch the user data.
      if (canParticipateInSection) {
        storeApi.page_group_users.list({
          section_user_id: sectionUserId,
          section_id: sectionId,
        });
        storeApi.page_groups.list({
          pagegroupuser__section_user_id: sectionUserId,
          section_id: sectionId,
        });
      }
    }
  }, [sectionUserId, sectionId, userId, canParticipateInSection]);
};

// TODO: Look into optimizing this.  We shouldn't be doing fetching for multiple sections and
// we shouldn't be doing them one section at a time.
/**
 * Fetches assessment data for multiple sections.
 * This is the core logic that both single-section and multi-section functions use.
 * If a student accesses this, the backend will limit the query to only be for their own data.
 */
const useFetchAssessmentDataForSections = (sectionIds: string[]) => {
  const userId = useAppSelector(selectCurrentUserId);

  useEffect(() => {
    if (!userId || sectionIds.length === 0) {
      return;
    }

    // Fetch data for all sections
    // TODO: In the future, this could be optimized to use a single batch API call
    // to reduce network overhead and improve performance.
    const uniqueSectionIds = [...new Set(sectionIds)];

    uniqueSectionIds.forEach((sectionId) => {
      storeApi.section_outcomes.list({ section_id: sectionId });
      storeApi.page_outcomes.list({ section_id: sectionId });
      storeApi.pages.list({ section_id: sectionId });
      // We don't limit this to official submissions, as we might want to show comments from
      // unofficial submission assessments as well.
      storeApi.submissions.list({ section_id: sectionId });
      storeApi.assessments.list({ section_id: sectionId });
      storeApi.outcome_assessments.list({ section_id: sectionId });
    });
  }, [sectionIds, userId]);
};

/**
 * Ensures all assessments-related data for the current course is loaded.
 */
export const useFetchAssessmentData = () => {
  const sectionId = useAppSelector(selectCurrentSectionId);
  useFetchAssessmentDataForSections(sectionId ? [sectionId] : []);
};

// Fetches and returns all assessments for the current user in the current course.
export const useFetchAndSelectMyAssessments = () => {
  const userId = useAppSelector(selectCurrentUserId);
  const sectionId = useAppSelector(selectCurrentSectionId);
  const assessments = useAppSelector((s) => selectUserAssessments(s, userId, sectionId));
  const { canParticipateInSection } = useCapabilities();

  useFetchAssessmentBaseData();

  // Only students can "fetch my assessments", as instructors don't get assessed,
  // and this would also query way too much data for admins.
  // TODO: Org-level admins (like MP admins) who are also students in a course will accidentally
  // fetch way too much data here.;
  useFetchAssessmentDataForSections(canParticipateInSection && sectionId ? [sectionId] : []);

  return assessments;
};

export const useFetchAndSelectMyAssessmentsInOrg = () => {
  const userId = useAppSelector(selectCurrentUserId);
  const sectionUsers = useListSelector(sectionUserSelectors.filterForCurrentUser);
  const allAssessments = useListSelector((s) => selectUserAssessments(s, userId));

  const sectionIds = useMemo(
    () => sectionUsers.filter((cu) => cu.role === SectionRole.STUDENT).map((cu) => cu.section_id),
    [sectionUsers],
  );

  useFetchAssessmentDataForSections(sectionIds);

  useEffect(() => {
    if (userId) {
      storeApi.section_users.list({ user_id: userId });
    }
  }, [userId]);

  return allAssessments;
};

export const useFetchPageAssessmentStatuses = (allPageIds: string[]) => {
  const pageIdString = allPageIds.sort().join(",");
  const userId = useAppSelector(selectCurrentUserId);
  const dispatch = useAppDispatch();

  useEffect(() => {
    if (!userId) {
      return;
    }

    const loadPageAssessmentStatusData = () => {
      const pageIds = pageIdString.split(",");

      // Batch size to avoid 413 errors
      const BATCH_SIZE = 150;

      // Split pageIds into batches
      const batches: string[][] = [];
      for (let i = 0; i < pageIds.length; i += BATCH_SIZE) {
        batches.push(pageIds.slice(i, i + BATCH_SIZE));
      }

      // Process all batches in parallel (fire and forget)
      batches.forEach(async (batch) => {
        const statuses = await serverGetPageAssessmentStatuses({ pageIds: batch });

        // Dispatch statuses immediately as each batch completes
        dispatch(computedStateActions.savePageAssessmentStatuses(statuses));
      });
    };

    if (pageIdString.length > 0) {
      loadPageAssessmentStatusData();
    }
  }, [pageIdString, userId, dispatch]);
};
